import { useCallback } from 'react';
import {
  DndContext, closestCenter, PointerSensor, KeyboardSensor, useSensor, useSensors,
} from '@dnd-kit/core';
import {
  arrayMove, SortableContext, verticalListSortingStrategy, useSortable,
  sortableKeyboardCoordinates,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { GripVertical } from 'lucide-react';

/**
 * Drag-and-drop wrapper around the saved-view group's rows. Renders a small
 * grip handle on the left of each row; reordering writes the new `manual_order`
 * back via the provided `onReorder({id, manual_order})` callback for each
 * affected row. Owner-only rows are draggable; shared/foreign rows render
 * without a grip and ignore drag attempts.
 */
const SortableRow = ({ id, isOwner, children }) => {
  const {
    attributes, listeners, setNodeRef, transform, transition, isDragging,
  } = useSortable({ id, disabled: !isOwner });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.6 : 1,
  };

  return (
    <div ref={setNodeRef} style={style} className="flex items-stretch">
      {isOwner ? (
        <button
          type="button"
          {...attributes}
          {...listeners}
          className="px-1 flex items-center cursor-grab active:cursor-grabbing text-muted-foreground/40 hover:text-muted-foreground"
          aria-label="Drag to reorder"
          data-testid={`saved-view-drag-${id}`}
        >
          <GripVertical className="h-3.5 w-3.5" />
        </button>
      ) : (
        <span className="px-1 w-[24px] flex-shrink-0" aria-hidden />
      )}
      <div className="flex-1 min-w-0">{children}</div>
    </div>
  );
};

const SortableViewGroup = ({ items, onReorder, renderRow }) => {
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const handleDragEnd = useCallback((event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = items.findIndex((i) => i.id === active.id);
    const newIndex = items.findIndex((i) => i.id === over.id);
    if (oldIndex < 0 || newIndex < 0) return;
    const next = arrayMove(items, oldIndex, newIndex);
    // Re-stamp manual_order in 10-step increments — gives room for inserts later.
    next.forEach((it, idx) => {
      if (it.is_owner) {
        onReorder({ id: it.id, manual_order: idx * 10 });
      }
    });
  }, [items, onReorder]);

  return (
    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
      <SortableContext items={items.map((i) => i.id)} strategy={verticalListSortingStrategy}>
        {items.map((v) => (
          <SortableRow key={v.id} id={v.id} isOwner={Boolean(v.is_owner)}>
            {renderRow(v)}
          </SortableRow>
        ))}
      </SortableContext>
    </DndContext>
  );
};

export default SortableViewGroup;
